import { Component, Inject, Output, EventEmitter, ViewChild, forwardRef, Injector } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslationService } from '../../services/languages/translation.service';
import { AppState } from '../../../store/state.model';
import { NgRedux } from '@angular-redux/store';
import { CommonDispatcher } from '../../../store/common/common-dispatcher';
import { AdminDispatcher } from '../../../store/admin-store/admin-dispatcher';
import { SidenavService } from '../../../admin/services/sidenav.service';
import { environment } from '../../../../environments/environment';
import { NgIf, NgFor } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatDialogClose } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-dialog-box',
    templateUrl: './dialog-box.component.html',
    styleUrls: ['./dialog-box.component.scss'],
    imports: [NgIf, MatButton, MatDialogClose, MatIcon, NgFor, TranslatePipe]
})
export class DialogBoxComponent {
  /**
  * @var DIALOG_MESSAGES to store the messages for each type of confirmation
  **/
  static DIALOG_MESSAGES = {
    'upload-failed': 'upload-failed.label.message',
    'go-back-upload': 'go-back.label.title',
    'go-back-view': 'go-back.label.title-view',
    'go-back-submit-error': 'go-back.label.title-view',
    'go-back-approve': 'go-back.label.approve',
    'go-back-reject': 'go-back.label.reject'
  };
  /**
  * @var API_ERRORS to store the messages for each error code from api
  **/
  static API_ERRORS = {
    'PROD-0006': 'no-packages',
    'PROD-0007': 'no-packages',
    'SVPP-0010': 'no-capabilities',
    'PROD-0001': 'no-generation',
    'SVPP-0008': 'no-generation',
    'VINGEN-NOTSUPPORTED': 'vingen-no-support',
    'INVALID-ENTRIES': 'invalid-combination'
  };
  /**
  * @var message to store the message for the corresponding message for the type of the dialog
  **/
  public message: string;
  /**
  * @var errorMessage to store the detailed error description of error
  **/
  public errorMessage;
  /**
  * @var apiName to store the api name
  **/
  public apiName;
  /**
  * @var vcmConfirmation to store the api name
  **/
  public vcmConfirmation: string[];
  private orchService: any;

  constructor(
    public dialogRef: MatDialogRef<DialogBoxComponent>,
    public translation: TranslationService,
    public router: Router,
    public ngRedux: NgRedux<AppState>,
    public commonDispatcher: CommonDispatcher,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public route: ActivatedRoute,
    private adminDispatcher: AdminDispatcher,
    private sidenav: SidenavService,
    private injector: Injector,
  ) {
    this.message = (data.type === 'api-error' ? this.getApiErrorMessage() 
    : (data.type === 'adf' || data.type === 'fdf' || data.type === 'partial fdf') ? "" : this.getDialogMessage(data.type))

    this.errorMessage = data.message;
    this.apiName = data.endPoint;
    if (this.isVcm) {
      this.vcmConfirmation = data.type === 'clear_vin' ? ['dialog-box.vcm.label.clear-vin', 'dialog-box.vcm.label.action.clear-vin'] : 
        data.type === 'clear_chip_id' ? ['dialog-box.vcm.label.clear-chip-id', 'dialog-box.vcm.label.action.clear-chip-id'] :
        ['dialog-box.vcm.label.black-list', 'dialog-box.vcm.label.action.black-list'];
    }
  }
  /**
  * @method getApiErrorMessage gets the user specific api error message
  * @returns string which contains message related to api error
  **/
  private getApiErrorMessage(): string {
    const errorCode = this.ngRedux.getState().common.errorCode;
    return !!DialogBoxComponent.API_ERRORS[errorCode] ?
      `dialog-box.api-error.${DialogBoxComponent.API_ERRORS[errorCode]}` : 'dialog-box.api-error.unknown';
  }
  /**
  * @method getDialogMessage gets the user specific message for errors
  * @param type type of error
  * @returns string which contains message based on condition
  **/
  private getDialogMessage(type: string): string {
    return !!DialogBoxComponent.DIALOG_MESSAGES[type] ?
    `dialog-box.${DialogBoxComponent.DIALOG_MESSAGES[type]}` : 'dialog-box.api-error.unknown';
  }
  /**
  * @method close closes the dialog box
  * @returns void
  **/
  public close(): void {
    this.dialogRef.close();
  }
  /**
  * @method leave redirects back to dashboard
  * @returns void
  */
  public leave(): void {
    this.commonDispatcher.showResults('false');
  }
  /**
  * @method continue emits true if selects yes to trigger the review call
  * @returns void
  */
  public continue(): void {
    this.dialogRef.close(true);
  }

  /**
* @method validate emits true if selects clear vin or blacklist button
* @returns void
*/
  public validate(flag: boolean): void {
    this.dialogRef.close(flag);
  }

 /**
  * @method isVcm emits true if it is vcm function
  * @returns boolean
  */
  public isVcm(): boolean {
    return ['black_list', 'clear_vin', 'clear_chip_id'].includes(this.data.type);
  }

  /**
  * @method isGoback method to determine is goback
  * @returns boolean returns true if type is either upload, view, submit-error, approve, reject
  */
  public isGoback(): boolean {
    return this.data.type === 'go-back-upload' || this.data.type === 'go-back-view' || this.data.type === 'go-back-submit-error'
      || this.data.type === 'go-back-approve' || this.data.type === 'go-back-reject';
  }
  /**
  * @method isAdmin method to determine is admin or submitter
  * @returns boolean returns true if type is either approve or reject
  */
  public isAdmin(): boolean {
    return this.data.type === 'go-back-approve' || this.data.type === 'go-back-reject';
  }

  /**
  * @method createCMS toggles the CMS section
  */
  public createCMS(): void {
    this.close();
    if(this.data.type === 'unlink-all'){
      this.sidenav.close();
    }
    if (this.canDisplayCMS()) {
      this.sidenav.toggle();
      window.scrollTo(0, 0);
      const unlinkAllErrorDes = this.data.type === 'unlink-all' ? this.errorMessage.map(function(message) { return '\n' + message.vin + `:` + message.type + `-` + message.response }) : '';
      this.adminDispatcher.setUpdateInfo('cms-ticket', 'cms-ticket', 'cms-ticket', '', { trigger: 'error-dialog', api: this.apiName, errorMessage: this.data.type === 'unlink-all' ?  unlinkAllErrorDes.join(', ') : this.errorMessage });
    }
  }
  /**
  * @method canDisplayCMS flag to display CMS button
  */
  public canDisplayCMS(): boolean {
    return window.location.pathname.includes('dashboard/admin') && this.ngRedux.getState().auth.isCMSAuthorized;
  }
  /**
  * @method deleteUser calls the delete user api
  */
  public async deleteUser(): Promise<void> {
    if (!this.orchService) {
      const { OrchestrationService } = await import('../../../user-role/services/orchestration-service/orchestration.service');
      this.orchService = this.injector.get(OrchestrationService);
    }
    this.orchService.deleteUser(this.data.userEmail);
    this.close();
  }
  /**
  * @method inactiveUser controls inactive user pops actions.
  */
  public inactiveUser(flag: boolean): void {
    this.dialogRef.close(flag);
  }
  /**
  * @method createTiket opens a link in a new browser to create ticket for inactive users
  **/
  public createTiket() {
    window.open(environment.service_desk, '_blank');
  }
}
